import { useEffect, useState } from 'react';
import type { DocsProduct } from './docs';

/**
 * Docs live in the website-combined repo and are indexed at build time with
 * Pagefind. We load that same index from the website so the app's search
 * always matches the published docs.
 */
export const DOCS_ORIGIN = 'https://www.todesktop.com';
const PAGEFIND_URL = `${DOCS_ORIGIN}/pagefind/pagefind.js`;
const MAX_RESULTS = 6;
const MIN_QUERY_LENGTH = 2;

interface PagefindResultData {
  url: string;
  excerpt?: string;
  meta?: { title?: string; product?: string };
}

interface PagefindModule {
  init?: () => Promise<void>;
  debouncedSearch: (
    term: string,
    options?: Record<string, unknown>,
    timeout?: number,
  ) => Promise<{
    results: { id: string; data: () => Promise<PagefindResultData> }[];
  } | null>;
}

export interface DocSearchResult {
  url: string;
  title: string;
  excerpt: string;
  product: 'Electron' | 'Builder';
}

const decodeEntities = (text: string) =>
  text
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

/** Maps a Pagefind result to plain text we can render without HTML. */
export const toDocSearchResult = (
  data: PagefindResultData,
): DocSearchResult => {
  const url = /^https?:\/\//.test(data.url)
    ? data.url
    : `${DOCS_ORIGIN}${data.url.startsWith('/') ? '' : '/'}${data.url}`;
  const path = url.replace(DOCS_ORIGIN, '');
  const excerpt = decodeEntities((data.excerpt || '').replace(/<[^>]*>/g, ''))
    // Heading anchors are indexed as "#Heading"; drop the marker.
    .replace(/(^|\s)#(?=\S)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  return {
    url,
    title: data.meta?.title?.trim() || path,
    excerpt,
    // Same rule as the docs site's own search: Electron pages say so (or live
    // under /electron/docs); everything else is Builder.
    product:
      data.meta?.product === 'electron' || path.startsWith('/electron/docs')
        ? 'Electron'
        : 'Builder',
  };
};

/** Keeps one product's results, in case a page wasn't tagged as expected. */
export const filterByProduct = (
  results: DocSearchResult[],
  product: DocsProduct,
): DocSearchResult[] =>
  results.filter(
    (result) => (result.product === 'Electron') === (product === 'electron'),
  );

let pagefindPromise: Promise<PagefindModule> | null = null;

const loadPagefind = (): Promise<PagefindModule> => {
  if (!pagefindPromise) {
    pagefindPromise = (async () => {
      const pagefind = (await import(
        /* @vite-ignore */ PAGEFIND_URL
      )) as PagefindModule;
      await pagefind.init?.();
      return pagefind;
    })().catch((error) => {
      // Let a later search retry, e.g. after a network blip.
      pagefindPromise = null;
      throw error;
    });
  }
  return pagefindPromise;
};

export interface DocsSearchState {
  results: DocSearchResult[];
  loading: boolean;
}

/**
 * Searches the published docs. Fails quietly: if the index can't be loaded the
 * palette still offers the static docs links.
 */
export const useDocsSearch = (
  query: string,
  enabled: boolean,
  /** Only search this product's docs; null searches both. */
  product: DocsProduct | null = null,
): DocsSearchState => {
  const [state, setState] = useState<DocsSearchState>({
    results: [],
    loading: false,
  });

  useEffect(() => {
    const term = query.trim();
    if (!enabled || term.length < MIN_QUERY_LENGTH) {
      setState({ results: [], loading: false });
      return undefined;
    }

    let cancelled = false;
    // Drop the previous term's results so they can't be opened while the new
    // search runs.
    setState({ results: [], loading: true });

    (async () => {
      try {
        const pagefind = await loadPagefind();
        // The docs site tags each page with its product, which Pagefind
        // can filter on (the site's own search does the same).
        const search = await pagefind.debouncedSearch(
          term,
          product ? { filters: { product } } : {},
          150,
        );
        if (cancelled) return;
        // Pagefind returns null when a newer search superseded this one.
        if (!search) {
          setState({ results: [], loading: false });
          return;
        }
        const data = await Promise.all(
          search.results.slice(0, MAX_RESULTS).map((result) => result.data()),
        );
        if (!cancelled) {
          const results = data.map(toDocSearchResult);
          setState({
            results: product ? filterByProduct(results, product) : results,
            loading: false,
          });
        }
      } catch {
        if (!cancelled) setState({ results: [], loading: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query, enabled, product]);

  return state;
};
